import { SOCIUU_FIELDS, SOCIUU_FIELDS_KEYS } from "./users-import.const";
import { IMappings, SizeUnit } from "./users-import.interface";

const hiddenCharRegex = /[^\x20-\x7E]/g;

export function convertSize(size: number, fromUnit: SizeUnit, toUnit: SizeUnit): number {
    if (fromUnit === toUnit) {
      return size;
    }

    if (fromUnit === SizeUnit.Bytes) {
      if (toUnit === SizeUnit.KB) {
        return size / 1024;
      } else if (toUnit === SizeUnit.MB) {
        return size / (1024 * 1024);
      }
    } else if (fromUnit === SizeUnit.KB) {
      if (toUnit === SizeUnit.Bytes) {
        return size * 1024;
      } else if (toUnit === SizeUnit.MB) {
        return size / 1024;
      }
    } else if (fromUnit === SizeUnit.MB) {
      if (toUnit === SizeUnit.Bytes) {
        return size * (1024 * 1024);
      } else if (toUnit === SizeUnit.KB) {
        return size * 1024;
      }
    }

    throw new Error('Invalid unit provided');
}

export function removeEmptyStringsFromEnd(arr: any): Array<any> {
  let i: number = arr.length - 1;

  // Iterate from the end of the array
  while (i >= 0 && arr[i] === "") {
      arr.pop(); // Remove the empty string from the end
      i--;
  }

  return arr;
}

export function checkDuplicateColumn(column: string): boolean {
  return false;
}

export function validateEmail(email: string): boolean {
  if (!email) return false;
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email?.toString().toLowerCase().trim());
}

export function checkValidPhoneNumber(phoneNumber: string) {
  if (!phoneNumber) return false;
  // Validate if the cleaned number contains only digits and an optional plus sign
  return /^\+?\d+$/.test(phoneNumber.toString().trim());
}

export function checkValidString(txt: string): boolean {
  txt = txt.toString().trim();
  return /^[a-z\s]{0,255}$/i.test(txt);
}

function testForHiddenCharactersAndEmpty(text: string): boolean {
  if (!text) return false;
  return true;
}

function testForHiddenCharacters(text: string): boolean {
  return !hiddenCharRegex.test(text.toString().trim());
}

export function checkValidStringEvenEmpty(text: string): boolean {
  // empety is not valid in this exp
  // return /^(?=.*[a-zA-Z0-9]).+$/.test(text);
  // empty string is valid in this case
  return /^[A-Za-z',\,.]{0,250}$/.test(text);
}

export function checkValidStringEvenEmptyForUserGroup(text: string): boolean {
  return /^[A-Za-z',\ ,;:]{0,250}$/.test(text);
}

export function checkValidDept(text: string): boolean {
  if (!text.trim().length) return false;
  else if (text.length > 255) return false;
  else return true;
}

function isStringLengthValid(text: string): boolean {
  return text && /^.{1,250}$/.test(text);
}

function nonEmptyAllowedCharsWithinRange(text: string): boolean {
  return text && /^[A-Za-z',\-_]{1,250}$/.test(text);
}

export function checkValidityOfColValue(
    column: string,
    value: string,
    isValidEmail: boolean,
    isValidPhone: boolean,
    isPhonePresent: boolean = false,
    isCountryCodePresent: boolean = false
  ): boolean {
  let isValidCell: boolean = false;
  switch(column) {
    case SOCIUU_FIELDS_KEYS.EMAIL:
      isValidCell = validateEmail(value) || (value === '' && isValidPhone);
      break;
    case SOCIUU_FIELDS_KEYS.FIRST_NAME:
    case SOCIUU_FIELDS_KEYS.LAST_NAME:
      isValidCell = /^.{1,250}$/s.test(value.toString().trim());
      break;
    case SOCIUU_FIELDS_KEYS.TITLE_1:
    case SOCIUU_FIELDS_KEYS.TITLE_2:
      isValidCell = /^.{0,250}$/s.test(value.toString().trim());
      break;
    case SOCIUU_FIELDS_KEYS.DEPT:
      isValidCell = /^.{1,250}$/s.test(value.toString().trim());
      break;
    case SOCIUU_FIELDS_KEYS.PHONE:
      isValidCell = value ? checkValidPhoneNumber(value) : (isValidEmail && !!!isCountryCodePresent);
      break;
    case SOCIUU_FIELDS_KEYS.PHONE_COUNTRY_CODE:
      isValidCell = value ? (/^.{1,2}$/s.test(value.toString().trim())) : !!!isPhonePresent;
      break;
    case SOCIUU_FIELDS_KEYS.USERGROUP:
      isValidCell = value.toString() === "" || testForHiddenCharactersAndEmpty(value);
      break;
  }
  return isValidCell;
}

function getKeyByValueFromSociuuFields(value: string): string | null {
  const fieldToGet = SOCIUU_FIELDS.find(field => field.value === value);

  if (fieldToGet) {
    return fieldToGet.key;
  }

  return null; // Return null if the value is not found
}

export function getTooltipText(columns: string, sociuuKey?: string, isDuplicate?: boolean): string {
  let tooltipText: string;
  const column: string = sociuuKey || getKeyByValueFromSociuuFields(columns);
  switch(column) {
    case SOCIUU_FIELDS_KEYS.EMAIL:
      if (isDuplicate) tooltipText = 'Duplicate Email Address';
      else tooltipText = 'Invalid email address, the email format must be "emil@example.com"';
      break;
    case SOCIUU_FIELDS_KEYS.FIRST_NAME:
      tooltipText = `Invalid or missing first name`;
      break;
    case SOCIUU_FIELDS_KEYS.LAST_NAME:
      tooltipText = `Invalid or missing last name`;
      break;
    case SOCIUU_FIELDS_KEYS.TITLE_1:
    case SOCIUU_FIELDS_KEYS.TITLE_2:
      tooltipText = 'Invalid Title';
      break;
    case SOCIUU_FIELDS_KEYS.DEPT:
      tooltipText = 'Invalid or missing department';
      break;
    case SOCIUU_FIELDS_KEYS.PHONE:
      if (isDuplicate) tooltipText = 'Duplicate Phone Number';
      else tooltipText = 'Invalid Phone';
      break;
    case SOCIUU_FIELDS_KEYS.PHONE_COUNTRY_CODE:
      tooltipText = 'Invalid Phone Country Code';
      break;
    case SOCIUU_FIELDS_KEYS.USERGROUP:
      tooltipText = 'Invalid Usergroup Data';
      break;
  }
  return tooltipText;
}

export function findDuplicateIndexes(arr: any[]): number[][] {
  const indexMap = new Map<any, number[]>();

  // Populate the map with indexes of each non-empty element
  arr.forEach((element, index) => {
    if (element !== '') {  // Exclude empty strings
      if (!indexMap.has(element)) {
        indexMap.set(element, []);
      }
      indexMap.get(element)!.push(index);
    }
  });

  // Filter out entries that have only one index (no duplicates)
  const duplicateIndexes = Array.from(indexMap.values()).filter(indexes => indexes.length > 1);

  return duplicateIndexes;
}

export function splitFullName(fullNames: string[], sliptChar: string = ' '): Array<{ firstName: string, lastName: string }> {
  let splittedArray: Array<{ firstName: string, lastName: string }> = fullNames.map(row => {
    let firstLast = row ? row.split(sliptChar, 2) : '';
    return {
      firstName: firstLast[0] || '',
      lastName: firstLast[1] || '',
    }
  });
  return splittedArray;
};


export function filterNumber(str: string): string {
  // Regular expression to match non-numeric characters
  const nonNumericRegex = /[^0-9]+/g;
  try {
    if (!str) return str;
    return str.toString().replace(nonNumericRegex, '')
  } catch (error) {
      console.error(error);
      return str;
  }
}

export function doesPhoneNumberExits(mappings: IMappings[]): number {
  return mappings
    .filter(coloumn => coloumn.state && coloumn?.sociuuProp?.key && coloumn.sociuuProp.key !== SOCIUU_FIELDS_KEYS.FULL_NAME)
    .map(coloumnNames => coloumnNames.sociuuProp.key)
    .findIndex(e => e === SOCIUU_FIELDS_KEYS.PHONE);
}
